import React, { forwardRef, useImperativeHandle, useState, useEffect, useLayoutEffect, useRef, useId } from 'react';
import { toast } from 'react-toastify';
import { creatorService } from '../../../../services/api';
import { Button } from '../../../ui/button';
import { useDirtyNavigationGuard } from '../../../routing/DirtyNavigationGuard';

const CreatorNotesTab = forwardRef(function CreatorNotesTab({ creator, onUpdate, readOnly = false, onDone, rows = 8,
  onNext, onPrevious, onSkip, busy = false, onRefresh }, ref) {
  const id = useId();
  const [notes, setNotes] = useState(creator?.notes || '');
  const [baseline, setBaseline] = useState(creator?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const leaving = useRef(false);
  const lastIncoming = useRef(creator?.notes || '');
  const dirty = notes !== baseline;
  const { setDirty, setInFlight, release, confirmAndRelease } = useDirtyNavigationGuard(`creator-notes-${id}`, {
    onDiscard: () => { setNotes(creator?.notes || ''); setBaseline(creator?.notes || ''); onDone?.(); }
  });

  useEffect(() => {
    if (lastIncoming.current === (creator?.notes || '')) return;
    lastIncoming.current = creator?.notes || '';
    if (dirty || submitting.current) return;
    setNotes(creator?.notes || '');
    setBaseline(creator?.notes || '');
  }, [creator?.notes, dirty]);

  useEffect(() => { setDirty(!readOnly && dirty); }, [dirty, readOnly, setDirty]);

  const requestLeave = async () => {
    if (submitting.current || busy || leaving.current) return false;
    leaving.current = true;
    try {
      if (!await confirmAndRelease('Discard these unsaved creator notes?')) return false;
      setNotes(creator?.notes || '');
      setBaseline(creator?.notes || '');
      setError('');
      return true;
    } finally { leaving.current = false; }
  };
  useImperativeHandle(ref, () => ({ requestLeave }));

  const move = async (action, event) => {
    if (event?.detail > 1 || !action || !await requestLeave()) return;
    submitting.current = true;
    setInFlight(true);
    try { await action(); }
    finally { submitting.current = false; setInFlight(false); }
  };

  const cancel = () => {
    if (submitting.current) return;
    setNotes(creator?.notes || '');
    setBaseline(creator?.notes || '');
    setError('');
    release();
    onDone?.();
  };

  const save = async (event) => {
    if (event?.detail > 1 || readOnly || busy || submitting.current || (!dirty && !onNext)) return;
    submitting.current = true;
    setInFlight(true);
    try {
      setSaving(true);
      setError('');
      if (dirty) {
        const result = await creatorService.updateCreator(creator._id, { notes, expectedNotes: baseline });
        const updated = result.creator || result;
        const savedNotes = updated.notes ?? notes;
        setNotes(savedNotes);
        setBaseline(savedNotes);
        onUpdate?.({ ...updated, _id: updated._id || creator._id, notes: savedNotes });
        toast.success('Notes saved');
      }
      release();
      if (onNext) { setInFlight(true); await onNext(); }
      else onDone?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save notes. Your draft is still here; try again.');
    } finally {
      submitting.current = false;
      setSaving(false);
      setInFlight(false);
    }
  };

  return (
    <div className="space-y-3">
      <label htmlFor={id} className="block text-sm font-medium text-text-primary">Creator database notes</label>
      <textarea id={id}
        value={notes}
        onChange={(e) => { setNotes(e.target.value); setDirty(e.target.value !== baseline); }}
        placeholder="Add notes about this creator..."
        rows={rows}
        maxLength={20000}
        autoFocus={!!onDone || !!onNext}
        readOnly={readOnly}
        disabled={saving || busy}
        className="w-full bg-surface-2 border border-glass-border p-3 text-base sm:text-sm text-text-primary placeholder:text-text-tertiary resize-y focus:ring-2 focus:ring-ring"
      />
      {dirty && (creator?.notes || '') !== baseline && <p className="text-sm text-warning">The database notes changed while you were editing. Your draft is preserved; cancel to load the latest notes.</p>}
      {error && <div role="alert" className="space-y-2 text-sm text-error"><p>{error}</p>{onRefresh && <Button type="button" variant="outline" onClick={event => move(onRefresh, event)} disabled={busy || saving}>Reload latest notes</Button>}</div>}
      {!readOnly && <div className="flex flex-wrap items-center gap-2">
        {onNext && <>
          <Button type="button" variant="outline" onClick={event => move(onPrevious, event)} disabled={!onPrevious || saving || busy} className="min-h-11">Previous</Button>
          <Button type="button" variant="ghost" onClick={event => move(onSkip, event)} disabled={saving || busy} className="min-h-11">Skip</Button>
          <span className="flex-1" />
        </>}
        <Button type="button" onClick={save} disabled={saving || busy || (!dirty && !onNext) || notes.length > 20000} size="sm" className="min-h-11">
          {saving ? 'Saving…' : onNext ? 'Save & next' : 'Save notes'}
        </Button>
        {!onNext && <Button type="button" variant="outline" onClick={cancel} disabled={saving || busy} size="sm" className="min-h-11">Cancel</Button>}
      </div>}
      {notes.length > 20000 && <p role="alert" className="text-sm text-error">Notes exceed 20,000 characters. Shorten them before saving; the existing text has not been truncated.</p>}
    </div>
  );
});

export default CreatorNotesTab;

export function CreatorNotesPreview({ creator, onUpdate, readOnly, editing = false, onEditingChange, canStartEdit = true }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const preview = useRef(null);
  useLayoutEffect(() => {
    if (editing || expanded || !preview.current) return undefined;
    const element = preview.current;
    const measure = () => setOverflowing(element.scrollHeight > element.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [creator.notes, editing, expanded]);
  if (editing) return <CreatorNotesTab key={creator._id} creator={creator} onUpdate={onUpdate} readOnly={readOnly} rows={5} onDone={() => onEditingChange(false)} />;
  return <div className="min-w-0 space-y-1">
    <p ref={preview} className={`whitespace-pre-wrap break-words text-sm leading-relaxed ${expanded ? '' : 'line-clamp-4'} ${creator.notes ? 'text-text-primary' : 'text-text-tertiary'}`}>{creator.notes || 'No notes yet'}</p>
    <div className="flex flex-wrap gap-3">
      {(overflowing || expanded || creator.notes?.length > 180 || creator.notes?.split('\n').length > 4) && <button type="button" className="min-h-11 text-xs text-text-secondary" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>{expanded ? 'Show less' : 'Read full note'}</button>}
      {!readOnly && <button type="button" disabled={!canStartEdit} className="min-h-11 text-sm text-accent disabled:opacity-50" aria-label={`Edit notes for @${creator.twitter?.username || 'creator'}`} onClick={() => onEditingChange(true)}>{creator.notes ? 'Edit notes' : 'Add notes'}</button>}
    </div>
  </div>;
}
